import React, { PureComponent, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import ResizeSensor from 'resize-sensor';
import { Modal, Button, Popup, Icon } from 'semantic-ui-react';
import { SketchPicker } from 'react-color';
import SketchCanvas from './SketchCanvas';
import { uploadFileFromBase64, FileManagementFileType } from '../../../helpers/FileUtility';
import { rescaleImageInModal, cropImage, loadImage } from '../../../helpers/ImageUtility';
import ImageModal from './ImageModal';
import Loader from '../../../components/loader';
import { SketchMode } from '../../../sketch/Sketch';
import '../../styles/SketchModal.scss';
import { CropDimensions, GetSharedFormImageUrlFunc, SketchValue, ValueChangedForFieldFunc, ValueChangedPayload } from '../../formeditor-types';
import { RootState, SasToken } from 'gf-types';
import { TemplatePage } from '../../../types/template-types';

export interface SketchModalProps {
  valueUpdated?: ValueChangedForFieldFunc;
  w?: number;
  h?: number;
  align?: string;
  value?: string;
  sasToken?: SasToken;
  onClose?: VoidFunc;
  formId?: string;
  croppedBackgroundImage?: string;
  fieldId?: string;
  rowNumber?: number | string;
  fileUrls?: Record<string, string>;
  getSharedFormImageUrl?: GetSharedFormImageUrlFunc;
  backgroundCropDimensions?: CropDimensions;
  pages?: TemplatePage[];
  imageQuality?: string;
  dateStamp?: boolean;
  fontSize?: number;
  fontName?: string;
  color?: string;
}

export type SketchModalState = {
  uploading: boolean;
  strokeSize: number;
  imagePickerOpen: boolean;
  confirmClearOpen: boolean;
  color: string;
  tool: SketchMode;
  pickerTool: SketchMode;
  croppedImg: string;
  editVisible: boolean;
  scaledSketchDimensions?: { height: number; width: number };
  resizedWidth?: number;
  resizedHeight?: number;
  undoAvailable: boolean;
  redoAvailable: boolean;
  containsImage: boolean;
  objectSelected: boolean;
};

//@ts-ignore - FIX
@connect((state: RootState) => ({
  sasToken: state.shared.sasToken,
  pages: state.formeditor.template?.pages
}))
export default class SketchModal extends PureComponent<SketchModalProps, SketchModalState> {
  private resizeSensor: TypeMePlease;
  private sketchCanvas: SketchCanvas;
  private modal: TypeMePlease;
  private sketchPicker: TypeMePlease;

  constructor(props) {
    super(props);

    this.handleDialogModalClose = this.handleDialogModalClose.bind(this);
    this.handleClearButtonClicked = this.handleClearButtonClicked.bind(this);
    this.handleCancelButtonClicked = this.handleCancelButtonClicked.bind(this);
    this.handleDoneButtonClicked = this.handleDoneButtonClicked.bind(this);
    this.handleConfirmClearClicked = this.handleConfirmClearClicked.bind(this);
    this.handleCancelClearClicked = this.handleCancelClearClicked.bind(this);
    this.openImagePicker = this.openImagePicker.bind(this);
    this.closeImagePicker = this.closeImagePicker.bind(this);
    this.setTool = this.setTool.bind(this);
    this.setColor = this.setColor.bind(this);
    this.setSize = this.setSize.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.imageUploaded = this.imageUploaded.bind(this);
    this.setCroppedBackgroundImage = this.setCroppedBackgroundImage.bind(this);

    this.state = {
      uploading: false,
      strokeSize: 4,
      imagePickerOpen: false,
      confirmClearOpen: false,
      scaledSketchDimensions: null,
      color: '#000000',
      tool: SketchMode.FREEDRAW,
      pickerTool: SketchMode.FREEDRAW,
      croppedImg: '',
      editVisible: false,
      undoAvailable: false,
      redoAvailable: false,
      containsImage: false,
      objectSelected: false
    };
  }

  async componentDidMount() {
    const d = document;
    const body = d.getElementsByTagName('body')[0];

    this.resizeSensor = new ResizeSensor(body, this.updateDimensions);

    this.updateDimensions();
    await this.setCroppedBackgroundImage();
  }

  componentWillUnmount() {
    if (this.resizeSensor) {
      this.resizeSensor.detach();
    }
  }

  handleClearButtonClicked() {
    this.setState({ confirmClearOpen: true });
  }

  handleConfirmClearClicked() {
    this.sketchCanvas.deleteAllObjects();

    this.setState({ confirmClearOpen: false });
  }

  handleCancelClearClicked() {
    this.setState({ confirmClearOpen: false });
  }

  handleDialogModalClose() {
    this.props.onClose();
  }

  handleCancelButtonClicked() {
    this.props.onClose();
  }

  async handleDoneButtonClicked() {
    const value = this.sketchCanvas.getValue();
    this.setState({ uploading: true });

    let fieldId = this.props.fieldId;
    if (this.props.rowNumber !== undefined) {
      fieldId += `_${this.props.rowNumber}`;
    }
    fieldId += '_DrawFileId';
    await uploadFileFromBase64(
      value.DrawingImage,
      this.props.w,
      this.props.h,
      true,
      true,
      this.props.formId,
      FileManagementFileType.Embedded,
      null,
      null,
      null,
      fieldId
    )
      .then((result) => {
        const jsonValue: SketchValue = {
          Value: value.Value,
          DrawFileId: result.fileId,
          Version: 2,
          FileId: value.FileId
        };
        if (this.props.fileUrls) {
          this.props.getSharedFormImageUrl(result.fileId);
        }
        this.props.valueUpdated({ value: JSON.stringify(jsonValue) });
      })
      .finally(() => {
        this.setState({ uploading: false });
      });

    this.props.onClose();
  }

  setTool(tool: SketchMode) {
    if (tool !== SketchMode.MOVEBEHIND && tool !== SketchMode.MOVEFRONT) {
      this.setState({ tool, pickerTool: tool === SketchMode.NONE ? this.state.pickerTool : tool });
    }

    this.sketchCanvas.setTool(tool);
  }

  setColor(color) {
    this.sketchCanvas.setColor(color);
    this.setState({ color });
  }

  setSize(size: number) {
    this.sketchCanvas.setSize(size);
    this.setState({ strokeSize: size });
  }

  openImagePicker() {
    this.setState({ imagePickerOpen: true });
  }

  closeImagePicker() {
    this.setState({ imagePickerOpen: false });
  }

  imageUploaded(value: ValueChangedPayload) {
    if (!value?.value || value?.value === '') {
      this.sketchCanvas.clearBackgroundImage();
      this.setState({ imagePickerOpen: false });
    } else {
      const jsonValue = JSON.parse(value.value);
      this.sketchCanvas.setBackgroundImage(jsonValue.FileId);
    }
  }

  async setCroppedBackgroundImage() {
    if (this.props.backgroundCropDimensions && this.props.pages) {
      const { x, y, w, h, pageNumber } = this.props.backgroundCropDimensions;

      const fileId = this.props.pages.find((page) => page.number === pageNumber).fileId;
      const imageData = !!this.props.fileUrls ? this.props.fileUrls[fileId] : `${this.props.sasToken.baseUrl}${fileId}${this.props.sasToken.token}`;
      const image = await loadImage(imageData, true);
      const croppedImg = await cropImage(image, x, y, w, h);
      this.setState({ croppedImg });
    }
  }

  updateDimensions() {
    if (!this.modal.ref) {
      return;
    }

    const { resizedWidth, resizedHeight } = rescaleImageInModal(this.props.w, this.props.h, this.modal.ref.current.offsetWidth);
    this.setState({ resizedWidth, resizedHeight });
    if (this.sketchCanvas) {
      this.sketchCanvas.resize(resizedWidth);
    }
  }

  render() {
    const scaledHeight = this.state.scaledSketchDimensions ? this.state.scaledSketchDimensions.height : this.props.h;
    const scaledWidth = this.state.scaledSketchDimensions ? this.state.scaledSketchDimensions.width : this.props.w;

    const confirmClearModal = (
      <Modal dimmer="inverted" size="tiny" open={this.state.confirmClearOpen} className="sketch-modal">
        <Modal.Header>Clear Sketch</Modal.Header>
        <Modal.Content>Clearing your sketch will remove all sketched content, but will not affect images. Manage images in the Image menu.</Modal.Content>
        <Modal.Actions>
          <Button onClick={this.handleCancelClearClicked} className="cancel">
            Cancel
          </Button>
          <Button onClick={this.handleConfirmClearClicked} className="done">
            Clear
          </Button>
        </Modal.Actions>
      </Modal>
    );

    let imageModal = null;
    if (this.state.imagePickerOpen) {
      imageModal = (
        <ImageModal
          promptDeleteExisting={this.state.containsImage}
          valueUpdated={this.imageUploaded}
          onClose={this.closeImagePicker}
          imageQuality={this.props.imageQuality}
          width={this.props.w}
          height={this.props.h}
          formId={this.props.formId}
          fieldName="Image Background"
          fieldId={this.props.fieldId}
          rowNumber={this.props.rowNumber as number}
          isSketchBackground={true}
          fileUrls={this.props.fileUrls}
          getSharedFormImageUrl={this.props.getSharedFormImageUrl}
          dateStamp={this.props.dateStamp}
          fontSize={this.props.fontSize}
          fontName={this.props.fontName}
          color={this.props.color}
        />
      );
    }
    return (
      <Modal
        open
        onClose={this.handleDialogModalClose}
        dimmer="inverted"
        size="tiny"
        className="sketch-modal"
        closeOnDimmerClick={false}
        ref={(r) => {
          this.modal = r;
        }}
      >
        <Modal.Header>
          Sketch
          <div className="header-buttons">
            <button onClick={() => this.sketchCanvas.undo()} title="Undo" disabled={!this.state.undoAvailable}>
              <div className="sketch-undo" />
            </button>
            <button onClick={() => this.sketchCanvas.redo()} title="Redo" disabled={!this.state.redoAvailable}>
              <div className="sketch-redo" />
            </button>
            <button onClick={() => this.openImagePicker()} title="Upload Image">
              <div className="sketch-full-image" />
            </button>
            <Button onClick={this.handleClearButtonClicked} className="clear">
              Clear Sketch
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content>
          <div className="button-toolbar">
            <button
              onClick={() => this.setTool(SketchMode.NONE)}
              className={`picker-button select-picker-button${this.state.tool === SketchMode.NONE ? ' selected' : ''}`}
            >
              <SelectPickerIcon />
            </button>
            <ToolSelectorButton tool={this.state.pickerTool} setTool={this.setTool} selected={this.state.tool !== SketchMode.NONE} />
            {this.state.tool !== SketchMode.NONE && !this.state.objectSelected ? (
              <>
                <ColorPickerButton color={this.state.color} setColor={this.setColor} />
                <SizePickerButton size={this.state.strokeSize} setSize={this.setSize} />
              </>
            ) : null}

            <div className="spacer"></div>
            {this.state.editVisible ? (
              <button onClick={() => this.sketchCanvas.openTextPromptForCurrentSelection()} title="Edit Shape">
                <div className="sketch-edit" />
              </button>
            ) : null}
            {this.state.objectSelected ? (
              <>
                <ColorPickerButton color={this.state.color} setColor={this.setColor} />
                <SizePickerButton size={this.state.strokeSize} setSize={this.setSize} />
                <button onClick={() => this.sketchCanvas.deleteSelectedObject()} title="Delete Shape">
                  <div className="sketch-trash" />
                </button>
                <button onClick={() => this.sketchCanvas.bringForward()} title="Bring Forward">
                  <div className="sketch-bring-forward" />
                </button>
                <button onClick={() => this.sketchCanvas.sendBackwards()} title="Send Backward">
                  <div className="sketch-bring-backward" />
                </button>
              </>
            ) : null}
          </div>
          <div
            style={{
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: `${this.state.resizedHeight}px`,
                backgroundImage: this.state.croppedImg ? `url('${this.state.croppedImg}')` : null,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            />

            <SketchCanvas
              w={scaledWidth}
              h={scaledHeight}
              align={this.props.align}
              value={this.props.value}
              sasToken={this.props.sasToken}
              fileUrls={this.props.fileUrls}
              onAvailableOptionsChanged={(name, value) => {
                if (name === 'textSelected') {
                  this.setState({ editVisible: value });
                } else if (name === 'undoAvailable') {
                  this.setState({ undoAvailable: value });
                } else if (name === 'redoAvailable') {
                  this.setState({ redoAvailable: value });
                } else if (name === 'containsImage') {
                  this.setState({ containsImage: value });
                } else if (name === 'objectSelected') {
                  this.setState({ objectSelected: value });
                }
              }}
              ref={(r) => {
                this.sketchCanvas = r;
              }}
            />
          </div>
          {confirmClearModal}
          {imageModal}
          <Loader isLoading={this.state.uploading} />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.handleCancelButtonClicked} className="cancel">
            Cancel
          </Button>
          <Button onClick={this.handleDoneButtonClicked} className="done">
            Done
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

type ToolPickerButtonProps = {
  tool: SketchMode;
  setTool: (tool: SketchMode) => void;
  selected: boolean;
};

function ToolSelectorButton(props: ToolPickerButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClosePopupOnBlur(isOpen, setIsOpen, popupRef, triggerRef);

  const setTool = (tool: SketchMode) => {
    props.setTool(tool);
    setIsOpen(false);
  };

  if (!props.selected) {
    return (
      <button onClick={() => setTool(props.tool)} className="picker-button tool-picker-button">
        <ToolIcon tool={props.tool} />
      </button>
    );
  }

  const trigger = (
    <button className={`picker-button tool-picker-button${isOpen || props.selected ? ' selected' : ''}`} ref={triggerRef}>
      <ToolIcon tool={props.tool} />
      <Icon className="caret down" />
    </button>
  );

  return (
    <Popup
      trigger={trigger}
      on="click"
      style={{ zIndex: 2000, border: '0px', padding: '0px' }}
      basic
      position="bottom left"
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      open={isOpen}
      className="tool-picker-popup"
      offset={() => []}
    >
      <div className="tools" ref={popupRef}>
        <button onClick={() => setTool(SketchMode.FREEDRAW)}>
          <ToolIcon tool={SketchMode.FREEDRAW} />
          <span className="tool-label">Pencil</span>
        </button>
        <button onClick={() => setTool(SketchMode.ARROW)}>
          <ToolIcon tool={SketchMode.ARROW} />
          <span className="tool-label">Arrow</span>
        </button>
        <button onClick={() => setTool(SketchMode.HIGHLIGHT)}>
          <ToolIcon tool={SketchMode.HIGHLIGHT} />
          <span className="tool-label">Highlighter</span>
        </button>
        <button onClick={() => setTool(SketchMode.LINE)}>
          <ToolIcon tool={SketchMode.LINE} />
          <span className="tool-label">Line</span>
        </button>
        <button onClick={() => setTool(SketchMode.TEXT)}>
          <ToolIcon tool={SketchMode.TEXT} />
          <span className="tool-label">Text</span>
        </button>
        <button onClick={() => setTool(SketchMode.CIRCLE)}>
          <ToolIcon tool={SketchMode.CIRCLE} />
          <span className="tool-label">Circle</span>
        </button>
        <button onClick={() => setTool(SketchMode.FILL)}>
          <ToolIcon tool={SketchMode.FILL} />
          <span className="tool-label">Fill Color</span>
        </button>
        <button onClick={() => setTool(SketchMode.RECTANGLE)}>
          <ToolIcon tool={SketchMode.RECTANGLE} />
          <span className="tool-label">Rectangle</span>
        </button>
        <div></div>
        <button onClick={() => setTool(SketchMode.TRIANGLE)}>
          <ToolIcon tool={SketchMode.TRIANGLE} />
          <span className="tool-label">Triangle</span>
        </button>
      </div>
    </Popup>
  );
}

const presetColors = ['#FF0000', '#F5A623', '#21B635', '#9013FE', '#1241FF', '#000000', '#80808f', '#FFFFFF'];
type ColorPickerButtonProps = {
  color: string;
  setColor: (color: string) => void;
};

function ColorPickerButton(props: ColorPickerButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClosePopupOnBlur(isOpen, setIsOpen, popupRef, triggerRef);

  let style = null;
  if (props.color) {
    style = { backgroundColor: props.color };
  }

  const trigger = (
    <button className={`picker-button color-picker-button${isOpen ? ' selected' : ''}`} ref={triggerRef}>
      <div className="color-icon" style={style}></div>
      <Icon className="caret down" />
    </button>
  );

  return (
    <Popup
      trigger={trigger}
      on="click"
      style={{ zIndex: 2000, border: '0px', padding: '0px' }}
      basic
      position="bottom left"
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      open={isOpen}
      className="color-picker-popup"
      offset={() => []}
    >
      <div ref={popupRef}>
        <SketchPicker
          disableAlpha
          color={props.color}
          onChangeComplete={(color) => {
            let hexColor = color.hex;
            hexColor = hexColor.toUpperCase();
            props.setColor(hexColor);
          }}
          presetColors={presetColors}
        />
      </div>
    </Popup>
  );
}

type SizePickerButtonProps = {
  size: number;
  setSize: (size: number) => void;
};

function SizePickerButton(props: SizePickerButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClosePopupOnBlur(isOpen, setIsOpen, popupRef, triggerRef);

  const trigger = (
    <button className={`picker-button size-picker-button${isOpen ? ' selected' : ''}`} ref={triggerRef}>
      <SizePickerIcon />
      <Icon className="caret down" />
    </button>
  );

  return (
    <Popup
      trigger={trigger}
      on="click"
      style={{ zIndex: 2000, border: '0px', padding: '0px' }}
      basic
      position="bottom left"
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      open={isOpen}
      className="size-picker-popup"
      offset={() => []}
    >
      <div className="size-picker-content" ref={popupRef}>
        <div className="size-button-wrapper">
          {[2, 3, 4, 6, 8, 10, 12, 14, 15, 16, 17, 18].map((i) => (
            <button key={i} onClick={() => props.setSize(i)} className={`${props.size === i ? 'selected' : ''}`}>
              <div style={{ width: `${i}px`, height: `${i}px`, borderRadius: `${i}px` }} className="size-dot"></div>
            </button>
          ))}
        </div>
      </div>
    </Popup>
  );
}

function ToolIcon(props: { tool: SketchMode }) {
  switch (props.tool) {
    case SketchMode.ARROW:
      return <ToolArrowIcon />;
    case SketchMode.LINE:
      return <ToolLineIcon />;
    case SketchMode.HIGHLIGHT:
      return <ToolHighlighterIcon />;
    case SketchMode.CIRCLE:
      return <ToolCircleIcon />;
    case SketchMode.TEXT:
      return <ToolTextIcon />;
    case SketchMode.RECTANGLE:
      return <ToolRectangleIcon />;
    case SketchMode.FILL:
      return <ToolFillIcon />;
    case SketchMode.TRIANGLE:
      return <ToolTriangleIcon />;
    case SketchMode.FREEDRAW:
    default:
      return <ToolPencilIcon />;
  }
}

function SizePickerIcon() {
  return (
    <svg width="24" height="21" viewBox="0 0 24 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="2.52632" fill="#333333" />
      <rect y="5.05249" width="24" height="5.05263" fill="#333333" />
      <rect y="12.6316" width="24" height="7.57895" fill="#333333" />
    </svg>
  );
}

function SelectPickerIcon() {
  return (
    <svg width="18" height="22" viewBox="0 0 23 27" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21.0402 15.5258L1.47795 1.75224L4.5642 25.7062L11.1642 17.4285L21.0402 15.5258Z" stroke="black" />
    </svg>
  );
}

function ToolPencilIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="pencil">
          <path
            d="M16.6590427,2.46447015 L17.3781645,1.74534835 C18.1871765,0.936336324 19.4456396,0.936336324 20.2546517,1.74534835 C21.0636637,2.55436037 21.0636637,3.81282352 20.2546517,4.62183554 L19.5355299,5.34095734"
            id="Shape"
            stroke="#000000"
            strokeWidth="1.5"
            className="strokeColor"
          ></path>
          <path d="M13.602775,4.53194532 L17.4680547,8.39722498" id="Shape" stroke="#000000" strokeWidth="1.5" className="strokeColor"></path>
          <polyline
            id="Shape"
            stroke="#000000"
            strokeWidth="1.5"
            points="6.32166681 19.6335031 5.60254501 16.397455 2.36649692 15.6783332"
            className="strokeColor"
          ></polyline>
          <polygon
            id="Shape"
            stroke="#000000"
            className="strokeColor"
            strokeWidth="1.5"
            points="6.05199614 19.9031738 1.10803377 20.8919662 2.09682625 15.9480039 16.1197013 1.9251288 20.0748712 5.88029869"
          ></polygon>
          <polygon
            id="Shape"
            fill="#000000"
            fillRule="nonzero"
            points="4.25419164 20.2627347 1.10803377 20.8919662 1.73726535 17.7458084"
            className="fillColor"
          ></polygon>
        </g>
      </g>
    </svg>
  );
}

function ToolArrowIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="arrows_long_right" transform="translate(2.000000, 6.000000)" stroke="#000000" strokeWidth="2" className="strokeColor">
          <path d="M17.1320811,5 L0,5" id="Shape"></path>
          <polyline id="Shape" points="12.8373799 0 17.5097656 4.67238576 12.8373799 9.34477151"></polyline>
        </g>
      </g>
    </svg>
  );
}

function ToolHighlighterIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g
          id="chisel_tip_marker"
          transform="translate(11.313708, 8.435029) rotate(-45.000000) translate(-11.313708, -8.435029) translate(2.813708, 0.935029)"
          stroke="#000000"
          strokeWidth="1.5"
          className="strokeColor"
        >
          <polyline
            id="XMLID_772_"
            points="10.3908653 0 5.39785208 5.06048633 3.576077 9.31129484 1.88924823 10.9981236 5.19543263 14.304308 6.8822614 12.6174792 11.1330699 10.7957042 16.1935562 5.80269099"
          ></polyline>
          <path d="M7.28710031,3.1712381 L13.0223181,8.97392909" id="Shape" strokeLinejoin="round"></path>
          <path d="M5.39785208,5.06048633 L11.1330699,10.8631773" id="Shape" strokeLinejoin="round"></path>
          <polyline id="Shape" points="4.38575482 13.6970497 3.1712381 14.8440932 0.404838906 13.8994691 2.49650659 11.8078014"></polyline>
        </g>
      </g>
    </svg>
  );
}

function ToolLineIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="line" transform="translate(3.000000, 3.000000)" stroke="#000000" strokeWidth="1.5" className="strokeColor">
          <path d="M0,15 L15,0" id="Shape"></path>
        </g>
      </g>
    </svg>
  );
}

function ToolTextIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="type" transform="translate(3.000000, 3.000000)" stroke="#000000" strokeWidth="2" className="strokeColor">
          <path d="M8,17 L8,0" id="Shape"></path>
          <path d="M0,0 L16,0" id="Shape"></path>
        </g>
      </g>
    </svg>
  );
}

function ToolCircleIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="circled" transform="translate(2.000000, 2.000000)" stroke="#000000" strokeWidth="1.5" className="strokeColor">
          <circle id="Oval" cx="9" cy="9" r="9"></circle>
        </g>
      </g>
    </svg>
  );
}

function ToolFillIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="fill_color" transform="translate(1.000000, 1.000000)" stroke="#333333" strokeWidth="1.5" className="strokeColor">
          <path
            d="M15,9.97142857 L7.02857143,2 L0.514285714,8.51428571 C-0.171428571,9.2 -0.171428571,10.3142857 0.514285714,11 L6,16.4857143 C6.68571429,17.1714286 7.8,17.1714286 8.48571429,16.4857143 L15,9.97142857 Z"
            id="Shape"
          ></path>
          <path d="M5,0 L7,2" id="Shape"></path>
          <path
            d="M19,18.3333333 C19,17.4166667 17.5,15 17.5,15 C17.5,15 16,17.4166667 16,18.3333333 C16,19.25 16.675,20 17.5,20 C18.325,20 19,19.25 19,18.3333333 Z"
            id="Shape"
          ></path>
        </g>
      </g>
    </svg>
  );
}

function ToolRectangleIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="rectangle" transform="translate(2.000000, 4.000000)" stroke="#000000" strokeWidth="1.5" className="strokeColor">
          <rect id="Rectangle-path" x="0" y="0" width="18" height="13"></rect>
        </g>
      </g>
    </svg>
  );
}

function ToolTriangleIcon() {
  return (
    <svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Artboard" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="triangle" transform="translate(2.000000, 4.000000)" stroke="#000000" strokeWidth="1.5" className="strokeColor">
          <polygon id="Shape" points="0 14 8.5 0 17 14"></polygon>
        </g>
      </g>
    </svg>
  );
}

function useClosePopupOnBlur(
  isOpen: boolean,
  setIsOpen: (boolean) => void,
  popupRef: React.MutableRefObject<HTMLElement>,
  triggerRef: React.MutableRefObject<HTMLElement>
) {
  useEffect(() => {
    let cancelled = false;
    if (isOpen) {
      const touchStartHandler = (e) => {
        if (
          !cancelled &&
          popupRef.current &&
          popupRef.current &&
          !popupRef.current.contains(e.target) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target)
        ) {
          setIsOpen(false);
        }
      };
      document.body.addEventListener('mousedown', touchStartHandler);
      document.body.addEventListener('touchstart', touchStartHandler);
      return () => {
        cancelled = true;
        document.body.removeEventListener('mousedown', touchStartHandler);
        document.body.removeEventListener('touchstart', touchStartHandler);
      };
    }
  }, [isOpen, setIsOpen, popupRef, triggerRef]);
}
